import { html, LitElement, PropertyValues } from 'lit';
import { customElement, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WEB_COMPONENT_GZ_EXCHANGE_SCAN_TASK } from '../../../../../../webcomponents.constants';
import {
  GzFormFieldToggleableWebComponent,
  ToggleType,
} from '../../../../../../ui/gzForm/formField/component/GzFormFieldToggleableWebComponent';
import { translateFn } from '../../../../../../utils/translator.helper';
import { GzInputNumberWebComponent, InputWidthValues } from '../../../../../../ui/gzInput';
import { SelectOption } from '../../../../../../ui/gzDropdown/types';
import {
  ExchangeScanTaskActions,
  ExchangeScanTaskTimeUnit,
  ExchangeTaskScanFileTypes,
  ExchangeTaskTargets,
  ExclusionGridItem,
  ExclusionsColumnKeys,
  ExclusionsLightGridEvents,
  ExclusionsTypes,
} from '../types';
import { WebComponentEvent } from '../../../../../../framework/BaseWebComponentController';
import { BaseColumnDetails } from '../../../../../../grid/types/common';
import { GzDropdownWebComponent } from '../../../../../../ui/gzDropdown';
import { GzInputWebComponent } from '../../../../../../ui/gzInput/component/GzInputWebComponent';
import { GzFormFieldWebComponent } from '../../../../../../ui/gzForm/formField';
import {
  ElementClass,
  elementClassListAdd,
  elementClassListRemove,
  elementClassListToggle,
} from '../../../../../../utils/htmlElement.utils';
import { ListItem } from '../../../../../../ui/gzSingleSelectList/types';
import { ButtonColor, ButtonFill, ButtonSize, ButtonType } from '../../../../../../ui/gzIconButton/types';
import { RequiredValidator } from '../../../../../../ui/gzForm/validators/required.validator';
import { FormGroup } from '../../../../../../ui/gzForm/formGroup/form-group';
import { Subscription } from 'rxjs';
import { GzCheckboxWebComponent } from '../../../../../../ui/gzCheckbox';
import { GzInputNumberWithDropdownWebComponent } from '../../gzInputNumberWithDropdown';
import { GzInputNumberWithDropdownFieldProps } from '../../gzInputNumberWithDropdown/types/types';
import { GzSingleSelectWebComponent } from '../../../../../../ui/gzSingleSelectDropdown';
import { EmailValidator } from '../../../../../../ui/gzForm/validators/email.validator';
import { CustomizableValidator } from '../../../../../../ui/gzForm/validators/customizable.validator';
import { ValidationResult } from '../../../../../../ui/gzForm/formField/types/form-validator';
import { GzIconButtonWebComponent } from '../../../../../../ui/gzIconButton';
import { FormGroupValue } from '../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { LightGridWebComponent } from '../../../../../../grid/lightGrid';
import { GzChipsGeneratorInputWebComponent } from '../../../../../../ui/gzChipsGeneratorInput';
import { NetworkPopUpEvents } from '../../../NetworkBasePopUp';
import { FontSizes, FontWeights, Keys, LabelWidthValues } from '../../../../../../types/common/types';
import { isEmpty, isEqual } from 'lodash';
import { NotInValuesValidator } from '../../../../../../ui/gzForm/validators/not-in-values.validator';
import { GzInputErrorWebComponent } from '../../../../../../ui/gzForm/inputError';
import { getFormattedDateForTask } from '../../../../../networkAppContainer/helpers/utils';

/**
 * @summary Content of the Exchange Scan Task pop-up
 * @tag gz-exchange-scan-task
 */
@customElement(WEB_COMPONENT_GZ_EXCHANGE_SCAN_TASK)
export class GzExchangeScanTaskWebComponent extends LitElement {
  // general form fields
  @query('gz-form-field#scan-name')
  private nameInput: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('gz-form-field-toggleable#stop-scan-task')
  private stopScanTaskToggleable: GzFormFieldToggleableWebComponent<
    GzInputNumberWithDropdownWebComponent,
    GzInputNumberWithDropdownFieldProps
  >;

  // scan target fields
  @query('gz-form-field#scan-targets-dropdown')
  scanTargetsDropdown: GzFormFieldWebComponent<GzDropdownWebComponent>;
  // objects checkboxes
  @query('gz-form-field#objects-email-checkbox')
  private objectsEmailCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('gz-form-field#objects-contacts-checkbox')
  private objectsContactsCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('gz-form-field#objects-tasks-checkbox')
  private objectsTasksCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('gz-form-field#objects-appointments-checkbox')
  private objectsAppointmentsCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('gz-form-field#objects-post-items-checkbox')
  private objectsPostItemsCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('#scan-objects-checkbox-error')
  private scanObjectsCheckboxError: GzInputErrorWebComponent;

  // restrictionsCheckboxes
  @query('gz-form-field#restrictions-unread-emails-checkbox')
  private restrictionsUnreadEmailsCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('gz-form-field#restrictions-items-with-attachments-checkbox')
  private restrictionsItemsWithAttachmentsCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('gz-form-field-toggleable#restrictions-items-received-last-checkbox')
  private restrictionsItemsReceivedLastToggleable: GzFormFieldToggleableWebComponent<
    GzInputNumberWithDropdownWebComponent,
    GzInputNumberWithDropdownFieldProps
  >;
  //exclusions form fields
  @query('gz-form-field#scan-exclusions-checkbox')
  private exclusionsCheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent>;
  @query('gz-form-field#exclusions-single-select')
  private exclusionsSingleSelect: GzFormFieldWebComponent<GzSingleSelectWebComponent>;
  @query('gz-form-field#exclusions-form-input')
  private exclusionsFormInput: GzFormFieldWebComponent<GzInputWebComponent>;
  @query('#add-exclusion-button')
  private addExclusionButton: GzIconButtonWebComponent;
  @query('gz-light-grid')
  private exclusionsLightGrid: LightGridWebComponent<ExclusionGridItem>;
  @query('#single-select-component')
  private singleSelectComponent: GzSingleSelectWebComponent;

  @query('#exclusions-group')
  private exclusionsGroup: HTMLElement;

  // scan options
  @query('gz-form-field#scan-file-types-dropdown')
  private scanFileTypesDropdown: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#extensions-group')
  private extensionsGroup: HTMLElement;
  @query('gz-form-field#extensions-form-input')
  private extensionsInput: GzFormFieldWebComponent<GzChipsGeneratorInputWebComponent>;
  @query('gz-form-field-toggleable#attachment-max-size')
  private attachmentMaxSizeToggleable: GzFormFieldToggleableWebComponent<GzInputNumberWebComponent, number>;
  @query('gz-form-field-toggleable#archive-maximum-depths')
  private archiveMaximumDepthsToggleable: GzFormFieldToggleableWebComponent<GzInputNumberWebComponent, number>;
  @query('gz-form-field#scan-for-pua-checkbox')
  private scanForPUACheckbox: GzFormFieldWebComponent<GzCheckboxWebComponent>;

  @query('.container')
  private container: HTMLElement;

  // scan actions
  @query('gz-form-field#default-action-infected-file-dropdown')
  private defaultActionInfectedFileDropdown: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('gz-form-field#default-action-suspect-file-dropdown')
  private defaultActionSuspectFileDropdown: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('gz-form-field#default-action-unscannable-file-dropdown')
  private defaultActionUnscannableFileDropdown: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('gz-form-field#alternative-action-infected-file-dropdown')
  private alternativeActionInfectedFileDropdown: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('gz-form-field#alternative-action-suspect-file-dropdown')
  private alternativeActionSuspectFileDropdown: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('gz-form-field#alternative-action-unscannable-file-dropdown')
  private alternativeActionUnscannableFileDropdown: GzFormFieldWebComponent<GzDropdownWebComponent>;

  @state()
  private formGroup: FormGroup;
  @state()
  private exclusionsForm: FormGroup;
  @state()
  private extensionsForm: FormGroup;
  @state()
  private scanObjectsForm: FormGroup;
  @state()
  private exclusionsGridItems: Array<ExclusionGridItem> = [];

  @state()
  private subscription: Subscription;

  // see https://lit-element.polymer-project.org/guide/styles
  static get styles() {
    return styles;
  }

  public getFormValue(): FormGroupValue {
    return this.formGroup.value;
  }

  public unsubscribeForms(): void {
    this.subscription.unsubscribe();
    this.formGroup.unsubscribe();
    this.exclusionsForm.unsubscribe();
    this.extensionsForm.unsubscribe();
    this.scanObjectsForm.unsubscribe();
  }

  public getExclusionsItems(): Array<ExclusionGridItem> {
    return this.exclusionsGridItems;
  }

  render() {
    return html`
      <div class="container">
        <div class="form-section mb-3" id="general-form">
          <div id="general-section">
            <gz-title
              class="form-subtitle title-container"
              fontweight="${FontWeights.MEDIUM}"
              fontSize="${FontSizes.HEADING_4}"
              >${translateFn(`General`)}
            </gz-title>

            <gz-form-field
              id="scan-name"
              labelWidth="${LabelWidthValues.LARGE}"
              label="${translateFn('Task name') + ':'}"
              class="mt-2"
            >
              <gz-input
                gz-form-input
                inputWidth="${InputWidthValues.LARGE}"
                value="${this.getExchangeScanTaskDefaultName()}"
                placeholder="${translateFn('Specify task name') + ':'}"
              >
              </gz-input>
            </gz-form-field>

            <gz-form-field-toggleable
              id="stop-scan-task"
              toggleType="${ToggleType.CHECKBOX}"
              label="${translateFn('Stop scan if it takes longer than') + ':'}"
            >
              <gz-input-number-with-dropdown
                class="label-mw-250"
                gz-form-input
                .inputMinValue="1"
                .inputDefaultValue="3"
                .inputWidth="${InputWidthValues.SMALL}"
                .dropdownOptions="${this.getStopScanAfterTimesUnit()}"
                .dropdownDefaultValue="${ExchangeScanTaskTimeUnit.HOURS}"
              ></gz-input-number-with-dropdown>
              <gz-input-error></gz-input-error>
            </gz-form-field-toggleable>
          </div>

          <div id="target-section">
            <gz-title
              class="form-subtitle title-container"
              fontweight="${FontWeights.MEDIUM}"
              fontSize="${FontSizes.HEADING_4}"
            >
              ${translateFn(`Target`)}
            </gz-title>

            <gz-form-field id="scan-targets-dropdown" label="${translateFn('Scan') + ':'}" class="mt-2 mb-2">
              <gz-dropdown
                gz-form-input
                options="${JSON.stringify(this.getExchangeTaskTargetsOptions())}"
                .value="${ExchangeTaskTargets.MAILBOXES_AND_PUBLIC_FOLDERS}"
                .placeholder="${translateFn(`Select scan target type`)}"
                @optionSelected="${this.handleScanTargetsDropdownChange}"
              ></gz-dropdown>
            </gz-form-field>

            <gz-title class="text-uppercase" fontweight="${FontWeights.SEMIBOLD}" fontSize="${FontSizes.PARAGRAPH_11}">
              ${translateFn('Objects')}
            </gz-title>

            <div id="scan-targets-checkbox-group">
              <gz-input-error id="scan-objects-checkbox-error"></gz-input-error>
              <gz-form-field id="objects-email-checkbox">
                <gz-checkbox
                  checked
                  gz-form-input
                  label="${translateFn('Email')}"
                  @change="${this.handleObjectsCheckboxChange}"
                ></gz-checkbox>
              </gz-form-field>

              <gz-form-field id="objects-contacts-checkbox">
                <gz-checkbox
                  checked
                  gz-form-input
                  label="${translateFn('Contacts')}"
                  @change="${this.handleObjectsCheckboxChange}"
                ></gz-checkbox>
              </gz-form-field>

              <gz-form-field id="objects-tasks-checkbox">
                <gz-checkbox
                  checked
                  gz-form-input
                  label="${translateFn('Tasks')}"
                  @change="${this.handleObjectsCheckboxChange}"
                ></gz-checkbox>
              </gz-form-field>

              <gz-form-field id="objects-appointments-checkbox">
                <gz-checkbox
                  checked
                  gz-form-input
                  label="${translateFn('Appointments')}"
                  @change="${this.handleObjectsCheckboxChange}"
                ></gz-checkbox>
              </gz-form-field>

              <gz-form-field id="objects-post-items-checkbox">
                <gz-checkbox
                  checked
                  gz-form-input
                  label="${translateFn('Post items')}"
                  @change="${this.handleObjectsCheckboxChange}"
                ></gz-checkbox>
              </gz-form-field>
            </div>

            <gz-title class="text-uppercase" fontweight="${FontWeights.SEMIBOLD}" fontSize="${FontSizes.PARAGRAPH_11}">
              ${translateFn('Restrictions')}
            </gz-title>
            <div id="scan-restrictions-checkbox-group">
              <gz-form-field id="restrictions-unread-emails-checkbox">
                <gz-checkbox gz-form-input label="${translateFn('Scan only unread emails')}"></gz-checkbox>
              </gz-form-field>

              <gz-form-field id="restrictions-items-with-attachments-checkbox">
                <gz-checkbox
                  checked
                  gz-form-input
                  label="${translateFn('Scan only items with attachments')}"
                ></gz-checkbox>
              </gz-form-field>

              <gz-form-field-toggleable
                labelWidth="${LabelWidthValues.LARGE}"
                id="restrictions-items-received-last-checkbox"
                toggleType="${ToggleType.CHECKBOX}"
                label="${translateFn('Scan only items received in the last') + ':'}"
              >
                <gz-input-number-with-dropdown
                  gz-form-input
                  .inputMinValue="1"
                  .inputDefaultValue="3"
                  .inputWidth="${InputWidthValues.SMALL}"
                  .dropdownOptions="${this.getStopScanAfterTimesUnit()}"
                  .dropdownDefaultValue="${ExchangeScanTaskTimeUnit.HOURS}"
                ></gz-input-number-with-dropdown>
                <gz-input-error></gz-input-error>
              </gz-form-field-toggleable>
            </div>
            <div id="exclusion-section">
              <gz-title
                class="text-uppercase mb-1"
                fontweight="${FontWeights.SEMIBOLD}"
                fontSize="${FontSizes.PARAGRAPH_11}"
              >
                ${translateFn('Exclusions')}
              </gz-title>
              <div id="scan-exclusions-checkbox-group">
                <gz-form-field id="scan-exclusions-checkbox">
                  <gz-checkbox
                    gz-form-input
                    label="${translateFn('Use exclusions for Exchange scan')}"
                    @change="${this.handleExclusionsCheckboxChange}"
                  ></gz-checkbox>
                </gz-form-field>

                <div id="exclusions-group" class="hidden">
                  <div id="grid-header">
                    <gz-form-field id="exclusions-single-select" class="mr-2">
                      <gz-single-select
                        gz-form-input
                        tabindex="0"
                        id="single-select-component"
                        .labelWidth=${LabelWidthValues.LARGE}
                        .filterOptions="${this.getExclusionsSingleSelectOptions()}"
                        .placeholder="${translateFn(`Select exclusion type`)}"
                        @selectionChange="${this.handleExclusionsSelectionChange}"
                      >
                      </gz-single-select>
                      <gz-input-error></gz-input-error>
                    </gz-form-field>

                    <gz-form-field id="exclusions-form-input" class="mb-2 mr-2">
                      <gz-input
                        gz-form-input
                        .labelWidth=${LabelWidthValues.LARGE}
                        placeholder="${translateFn('Specify task scope')}"
                        @change="${this.handleExclusionsFormInputChange}"
                        inputWidth="${InputWidthValues.LARGE}"
                      >
                      </gz-input>
                      <gz-input-error></gz-input-error>
                    </gz-form-field>

                    <gz-icon-button
                      id="add-exclusion-button"
                      disabled
                      icon="ds-icon-add"
                      fontSize="24"
                      buttontype="${ButtonType.SQUARE}"
                      buttonfill="${ButtonFill.PRIMARY}"
                      buttoncolor="${ButtonColor.WHITE}"
                      size="${ButtonSize.SIZE_32}"
                      @click="${this.addExclusion}"
                    >
                    </gz-icon-button>
                  </div>
                  <gz-light-grid
                    @removeExclusion="${this.removeExclusion}"
                    .items="${this.exclusionsGridItems}"
                  ></gz-light-grid>
                  ${this.renderEmptyGridState()}
                </div>
              </div>
            </div>
          </div>

          <div id="options-section" class="mt-4">
            <gz-title
              class="form-subtitle title-container"
              fontweight="${FontWeights.MEDIUM}"
              fontSize="${FontSizes.HEADING_4}"
            >
              ${translateFn(`Options`)}
            </gz-title>
            <gz-form-field id="scan-file-types-dropdown" class="mb-2" label="${translateFn('Scan file types') + ':'}">
              <gz-dropdown
                gz-form-input
                options="${JSON.stringify(this.getScanFileTypesOptions())}"
                .value="${ExchangeTaskScanFileTypes.ALL_FILES}"
                .placeholder="${translateFn(`File types to scan`)}"
                @optionSelected="${this.handleFileTypesToScanOptionChanged}"
              >
              </gz-dropdown>
            </gz-form-field>

            <div id="extensions-group" class="hidden">
              <gz-form-field
                id="extensions-form-input"
                class="mb-2"
                label="${translateFn('Extensions') + ':'}"
                manualValidation
                @form-value-valid="${() =>
                  this.dispatchEvent(
                    new CustomEvent(NetworkPopUpEvents.SET_CONFIRM_BUTTON, {
                      bubbles: true,
                      composed: true,
                      detail: { value: false },
                    })
                  )}"
                @form-value-invalid="${() =>
                  this.dispatchEvent(
                    new CustomEvent(NetworkPopUpEvents.SET_CONFIRM_BUTTON, {
                      bubbles: true,
                      composed: true,
                      detail: { value: true },
                    })
                  )}"
              >
                <gz-chips-generator-input
                  gz-form-input
                  ?themeInput="${true}"
                  @change="${e => this.handleChipsChange(e)}"
                  .customKeyUps="${[Keys.Space, Keys.Enter]}"
                  .notUniqueErrorMessage="${translateFn(`Extension already exists`)}"
                  .invalidItemErrorMessage="${translateFn(`Invalid extension`)}"
                  .placeholder="${translateFn(`Type extensions separated by a semicolon`)}"
                ></gz-chips-generator-input>
                <gz-input-error></gz-input-error>
              </gz-form-field>
            </div>

            <gz-form-field-toggleable
              id="attachment-max-size"
              toggleType="${ToggleType.CHECKBOX}"
              label="${translateFn('Attachment/email body maximum size (MB)')}"
            >
              <gz-input-number gz-form-input min="1" value="3" inputWidth="${InputWidthValues.SMALL}"></gz-input-number>
              <gz-input-error></gz-input-error>
            </gz-form-field-toggleable>

            <gz-form-field-toggleable
              isOn
              id="archive-maximum-depths"
              toggleType="${ToggleType.CHECKBOX}"
              label="${translateFn('Archive maximum depth (levels)')}"
            >
              <gz-input-number
                gz-form-input
                min="1"
                value="4"
                inputWidth="${InputWidthValues.SMALL}"
                max="16"
              ></gz-input-number>
              <gz-input-error></gz-input-error>
            </gz-form-field-toggleable>

            <gz-form-field id="scan-for-pua-checkbox">
              <gz-checkbox
                checked
                gz-form-input
                label="${translateFn('Scan for Potentially Unwanted Applications (PUA)')}"
                value="scanForPUA"
              ></gz-checkbox>
              <gz-input-error></gz-input-error>
            </gz-form-field>
          </div>

          <div id="actions-section" class="mt-4">
            <gz-title
              class="form-subtitle title-container mt-2 mb-2"
              fontweight="${FontWeights.MEDIUM}"
              fontSize="${FontSizes.HEADING_4}"
            >
              ${translateFn(`Actions`)}
            </gz-title>

            <gz-form-field
              id="default-action-infected-file-dropdown"
              class="mb-1"
              label="${translateFn('Default action for infected files') + ':'}"
            >
              <gz-dropdown
                gz-form-input
                options="${JSON.stringify(this.getInfectedFileActions())}"
                .value="${ExchangeScanTaskActions.REMEDIATE}"
                .placeholder="${translateFn(`Select default action`)}"
              >
              </gz-dropdown>
            </gz-form-field>

            <gz-form-field
              id="alternative-action-infected-file-dropdown"
              class="mb-4"
              label="${translateFn('Alternative action') + ':'}"
            >
              <gz-dropdown
                gz-form-input
                options="${JSON.stringify(this.getInfectedFileAlternativeActions())}"
                .value="${ExchangeScanTaskActions.MOVE_TO_QUARANTINE}"
                .placeholder="${translateFn(`Select alternative action`)}"
              >
              </gz-dropdown>
            </gz-form-field>

            <gz-form-field
              id="default-action-suspect-file-dropdown"
              class="mb-1"
              label="${translateFn('Default action for suspicious files') + ':'}"
            >
              <gz-dropdown
                gz-form-input
                @open="${() => this.scrollElementIntoView(this.defaultActionSuspectFileDropdown as HTMLElement)}"
                options="${JSON.stringify(this.getSuspectFileActions())}"
                .value="${ExchangeScanTaskActions.REPORT_ONLY}"
                .placeholder="${translateFn(`Select default action`)}"
              >
              </gz-dropdown>
            </gz-form-field>

            <gz-form-field
              id="alternative-action-suspect-file-dropdown"
              class="mb-4"
              label="${translateFn('Alternative action') + ':'}"
            >
              <gz-dropdown
                gz-form-input
                @open="${() => this.scrollElementIntoView(this.alternativeActionSuspectFileDropdown as HTMLElement)}"
                options="${JSON.stringify(this.getSuspectFileAlternativeActions())}"
                .value="${ExchangeScanTaskActions.REPORT_ONLY}"
                .placeholder="${translateFn(`Select alternative action`)}"
              >
              </gz-dropdown>
            </gz-form-field>

            <gz-form-field
              id="default-action-unscannable-file-dropdown"
              class="mb-1"
              label="${translateFn('Default action for unscannable files') + ':'}"
            >
              <gz-dropdown
                @open="${() => this.scrollElementIntoView(this.defaultActionUnscannableFileDropdown as HTMLElement)}"
                gz-form-input
                options="${JSON.stringify(this.getUnscannableFileActions())}"
                .value="${ExchangeScanTaskActions.REPORT_ONLY}"
                .placeholder="${translateFn(`Select default action`)}"
              >
              </gz-dropdown>
            </gz-form-field>

            <gz-form-field
              id="alternative-action-unscannable-file-dropdown"
              class="mb-4"
              label="${translateFn('Alternative action') + ':'}"
            >
              <gz-dropdown
                gz-form-input
                @open="${() =>
                  this.scrollElementIntoView(this.alternativeActionUnscannableFileDropdown as HTMLElement)}"
                options="${JSON.stringify(this.getUnscannableFileAlternativeActions())}"
                .value="${ExchangeScanTaskActions.REPORT_ONLY}"
                .placeholder="${translateFn(`Select alternative action`)}"
              >
              </gz-dropdown>
            </gz-form-field>
          </div>
        </div>
      </div>
    `;
  }

  protected firstUpdated(_changedProperties: PropertyValues) {
    super.firstUpdated(_changedProperties);
    this.setFormValidators();
    this.buildFormGroup();
    this.exclusionsLightGrid.columns = this.getExclusionsLightGridColumns();
    this.exclusionsLightGrid.columnRenderers = {
      ['type']: this.exclusionTypeRenderer,
      ['actions']: this.actionsRenderer,
    };
  }

  private renderEmptyGridState() {
    if (isEmpty(this.exclusionsGridItems)) {
      return html`<gz-button
        class="empty-state-grid"
        type="quiet"
        color="primary"
        buttonheight="small"
        buttonfontsize="small"
        lowercase
        @click="${this.handleGridEmptyStateBtn}"
      >
        ${translateFn('Add exclusions')}
      </gz-button>`;
    }
  }

  private handleGridEmptyStateBtn() {
    this.singleSelectComponent.focus();
  }

  protected async updated(_changedProperties: PropertyValues) {
    super.updated(_changedProperties);

    if (this.exclusionsLightGrid) {
      await this.exclusionsLightGrid.updateComplete;
      this.setGridRowBorders();
    }
  }

  private setFormValidators(): void {
    this.nameInput.Validators = [RequiredValidator(translateFn(`Task name is required`))];
  }

  private buildFormGroup(): void {
    this.exclusionsForm = FormGroup.build({
      exclusionsSingleSelect: this.exclusionsSingleSelect,
      exclusionsFormInput: this.exclusionsFormInput,
    });

    this.exclusionsForm.setDisabled(true);

    this.extensionsForm = FormGroup.build({
      extensions: this.extensionsInput,
    });

    this.extensionsForm.setDisabled(true);

    this.scanObjectsForm = FormGroup.build({
      scanEmail: this.objectsEmailCheckbox,
      scanContacts: this.objectsContactsCheckbox,
      scanTasks: this.objectsTasksCheckbox,
      scanAppointments: this.objectsAppointmentsCheckbox,
      scanPostItems: this.objectsPostItemsCheckbox,
    });

    this.formGroup = FormGroup.build({
      general: {
        name: this.nameInput,
        stopScanTask: this.stopScanTaskToggleable,
      },
      targetSettings: {
        scanTarget: this.scanTargetsDropdown, // might not be needed
        scanObjects: this.scanObjectsForm,
        scanRestrictions: {
          scanUnreadEmails: this.restrictionsUnreadEmailsCheckbox,
          scanItemsWithAttachments: this.restrictionsItemsWithAttachmentsCheckbox,
          scanItemsReceivedLast: this.restrictionsItemsReceivedLastToggleable,
        },
      },
      useExclusions: this.exclusionsCheckbox,
      exclusions: {
        exclusionsForm: this.exclusionsForm,
      },
      scanOptions: {
        scanFileTypes: this.scanFileTypesDropdown,
        useExtensions: this.extensionsForm,
        attachmentMaxSize: this.attachmentMaxSizeToggleable,
        archiveMaximumDepth: this.archiveMaximumDepthsToggleable,
        scanForPUA: this.scanForPUACheckbox,
      },
      scanActions: {
        defaultActionInfectedFile: this.defaultActionInfectedFileDropdown,
        defaultActionSuspectFile: this.defaultActionSuspectFileDropdown,
        defaultActionUnscannableFile: this.defaultActionUnscannableFileDropdown,
        alternativeActionInfectedFile: this.alternativeActionInfectedFileDropdown,
        alternativeActionSuspectFile: this.alternativeActionSuspectFileDropdown,
        alternativeActionUnscannableFile: this.alternativeActionUnscannableFileDropdown,
      },
    });

    this.subscription = this.formGroup.valid$.subscribe({
      next: (valid: boolean) => {
        let isValid = valid;

        const shouldUseExclusions = this.exclusionsCheckbox.Input.checked;
        if (shouldUseExclusions) {
          isValid = valid && !!this.exclusionsGridItems.length;
        }

        this.dispatchEvent(
          new CustomEvent(NetworkPopUpEvents.SET_CONFIRM_BUTTON, {
            bubbles: true,
            composed: true,
            detail: { value: !isValid },
          })
        );
      },
    });

    this.exclusionsForm.valid$.subscribe({
      next: (valid: boolean) => {
        this.addExclusionButton.disabled = !valid;
      },
    });
  }

  private getExchangeTaskTargetsOptions(): Array<SelectOption> {
    return [
      {
        title: translateFn('Mailboxes and public folders'),
        value: ExchangeTaskTargets.MAILBOXES_AND_PUBLIC_FOLDERS,
      },
      {
        title: translateFn('Mailboxes'),
        value: ExchangeTaskTargets.MAILBOXES,
      },
      {
        title: translateFn('Public folders'),
        value: ExchangeTaskTargets.PUBLIC_FOLDERS,
      },
    ];
  }

  private getScanFileTypesOptions(): Array<SelectOption> {
    return [
      {
        title: translateFn('All files'),
        value: ExchangeTaskScanFileTypes.ALL_FILES,
      },
      {
        title: translateFn('Applications only'),
        value: ExchangeTaskScanFileTypes.APPLICATIONS_ONLY,
      },
      {
        title: translateFn('User-defined extensions'),
        value: ExchangeTaskScanFileTypes.USER_DEFINED_EXTENSIONS,
      },
      {
        title: translateFn('All files except specific extensions'),
        value: ExchangeTaskScanFileTypes.ALL_FILES_EXCEPT_SPECIFIC_EXTENSIONS,
      },
    ];
  }

  private getStopScanAfterTimesUnit(): Array<SelectOption> {
    return [
      {
        title: translateFn('Hours'),
        value: ExchangeScanTaskTimeUnit.HOURS,
      },
      {
        title: translateFn('Days'),
        value: ExchangeScanTaskTimeUnit.DAYS,
      },
    ];
  }

  private getInfectedFileActions(): Array<SelectOption> {
    return [
      {
        title: translateFn('Remediate'),
        value: ExchangeScanTaskActions.REMEDIATE,
      },
      {
        title: translateFn('Reject/Delete email'),
        value: ExchangeScanTaskActions.REJECT_DELETE_EMAIL,
      },
      {
        title: translateFn('Delete file'),
        value: ExchangeScanTaskActions.DELETE_FILE,
      },
      {
        title: translateFn('Replace file'),
        value: ExchangeScanTaskActions.REPLACE_FILE,
      },
      {
        title: translateFn('Move to quarantine'),
        value: ExchangeScanTaskActions.MOVE_TO_QUARANTINE,
      },
      {
        title: translateFn('Report only'),
        value: ExchangeScanTaskActions.REPORT_ONLY,
      },
    ];
  }

  private getInfectedFileAlternativeActions(): Array<SelectOption> {
    return [
      {
        title: translateFn('Reject/Delete email'),
        value: ExchangeScanTaskActions.REJECT_DELETE_EMAIL,
      },
      {
        title: translateFn('Delete file'),
        value: ExchangeScanTaskActions.DELETE_FILE,
      },
      {
        title: translateFn('Replace file'),
        value: ExchangeScanTaskActions.REPLACE_FILE,
      },
      {
        title: translateFn('Move to quarantine'),
        value: ExchangeScanTaskActions.MOVE_TO_QUARANTINE,
      },
      {
        title: translateFn('Report only'),
        value: ExchangeScanTaskActions.REPORT_ONLY,
      },
    ];
  }

  private getSuspectFileActions(): Array<SelectOption> {
    return [
      {
        title: translateFn('Reject/Delete email'),
        value: ExchangeScanTaskActions.REJECT_DELETE_EMAIL,
      },
      {
        title: translateFn('Delete file'),
        value: ExchangeScanTaskActions.DELETE_FILE,
      },
      {
        title: translateFn('Replace file'),
        value: ExchangeScanTaskActions.REPLACE_FILE,
      },
      {
        title: translateFn('Move to quarantine'),
        value: ExchangeScanTaskActions.MOVE_TO_QUARANTINE,
      },
      {
        title: translateFn('Report only'),
        value: ExchangeScanTaskActions.REPORT_ONLY,
      },
    ];
  }

  private getSuspectFileAlternativeActions(): Array<SelectOption> {
    return [
      {
        title: translateFn('Reject/Delete email'),
        value: ExchangeScanTaskActions.REJECT_DELETE_EMAIL,
      },
      {
        title: translateFn('Delete file'),
        value: ExchangeScanTaskActions.DELETE_FILE,
      },
      {
        title: translateFn('Replace file'),
        value: ExchangeScanTaskActions.REPLACE_FILE,
      },
      {
        title: translateFn('Move to quarantine'),
        value: ExchangeScanTaskActions.MOVE_TO_QUARANTINE,
      },
      {
        title: translateFn('Report only'),
        value: ExchangeScanTaskActions.REPORT_ONLY,
      },
    ];
  }

  private getUnscannableFileActions(): Array<SelectOption> {
    return [
      {
        title: translateFn('Reject/Delete email'),
        value: ExchangeScanTaskActions.REJECT_DELETE_EMAIL,
      },
      {
        title: translateFn('Delete file'),
        value: ExchangeScanTaskActions.DELETE_FILE,
      },
      {
        title: translateFn('Replace file'),
        value: ExchangeScanTaskActions.REPLACE_FILE,
      },
      {
        title: translateFn('Move to quarantine'),
        value: ExchangeScanTaskActions.MOVE_TO_QUARANTINE,
      },
      {
        title: translateFn('Report only'),
        value: ExchangeScanTaskActions.REPORT_ONLY,
      },
    ];
  }

  private getUnscannableFileAlternativeActions(): Array<SelectOption> {
    return [
      {
        title: translateFn('Reject/Delete email'),
        value: ExchangeScanTaskActions.REJECT_DELETE_EMAIL,
      },
      {
        title: translateFn('Delete file'),
        value: ExchangeScanTaskActions.DELETE_FILE,
      },
      {
        title: translateFn('Replace file'),
        value: ExchangeScanTaskActions.REPLACE_FILE,
      },
      {
        title: translateFn('Move to quarantine'),
        value: ExchangeScanTaskActions.MOVE_TO_QUARANTINE,
      },
      {
        title: translateFn('Report only'),
        value: ExchangeScanTaskActions.REPORT_ONLY,
      },
    ];
  }

  private handleExclusionsCheckboxChange(event: WebComponentEvent): void {
    elementClassListToggle(this.exclusionsGroup, ElementClass.HIDDEN);
    if (!event.detail.checked) {
      event.target.dispatchEvent(
        new CustomEvent(NetworkPopUpEvents.SET_CONFIRM_BUTTON, {
          bubbles: true,
          composed: true,
          detail: { value: !this.formGroup.valid },
        })
      );
      this.exclusionsForm.setDisabled(true);
    } else {
      event.target.dispatchEvent(
        new CustomEvent(NetworkPopUpEvents.SET_CONFIRM_BUTTON, {
          bubbles: true,
          composed: true,
          detail: { value: !this.exclusionsGridItems.length },
        })
      );
      this.exclusionsSingleSelect.Validators = this.exclusionsGridItems.length
        ? []
        : [RequiredValidator(translateFn(`Select exclusion type`))];
      this.exclusionsForm.setDisabled(false);
    }
  }

  private handleObjectsCheckboxChange(event: WebComponentEvent) {
    const scanObjectsCheckboxGroupValue = this.scanObjectsForm.value;
    const isAnyChecked = Object.values(scanObjectsCheckboxGroupValue).some((value: boolean) => value);

    if (isAnyChecked) {
      if (this.formGroup.valid) {
        event.target.dispatchEvent(
          new CustomEvent(NetworkPopUpEvents.SET_CONFIRM_BUTTON, {
            bubbles: true,
            composed: true,
            detail: { value: false },
          })
        );
      }

      this.scanObjectsCheckboxError.Error = null;
      return;
    }

    event.target.dispatchEvent(
      new CustomEvent(NetworkPopUpEvents.SET_CONFIRM_BUTTON, {
        bubbles: true,
        composed: true,
        detail: { value: true },
      })
    );
    this.scanObjectsCheckboxError.Error = translateFn(`At least one object must be selected!`);
  }

  private getExclusionsSingleSelectOptions(): Array<ListItem> {
    return [
      {
        label: translateFn('Database'),
        value: ExclusionsTypes.DATABASE,
      },
      {
        label: translateFn('Mailbox'),
        value: ExclusionsTypes.MAILBOX,
      },
      {
        label: translateFn('Public folder'),
        value: ExclusionsTypes.PUBLIC_FOLDER,
      },
    ];
  }

  private getExclusionsLightGridColumns(): Array<BaseColumnDetails> {
    return [
      {
        key: ExclusionsColumnKeys.TYPE,
        name: translateFn('Exclusions Type'),
        width: '142px',
      },
      {
        key: ExclusionsColumnKeys.SCOPE,
        name: translateFn('Scope'),
        width: '354px',
      },
      {
        key: ExclusionsColumnKeys.ACTIONS,
        name: translateFn('Actions'),
      },
    ];
  }

  private exclusionTypeRenderer(item, columnDetails, rowData) {
    const exclusionType = rowData.item[columnDetails.key];

    switch (exclusionType) {
      case ExclusionsTypes.DATABASE:
        return html`${translateFn('Database')}`;
      case ExclusionsTypes.MAILBOX:
        return html`${translateFn('Mailbox')}`;
      case ExclusionsTypes.PUBLIC_FOLDER:
        return html`${translateFn('Public folder')}`;
    }
  }

  private actionsRenderer(item, columnDetails, rowData) {
    return html` <span part="actions-column">
      <gz-icon-button
        @click="${() => {
          item.dispatchEvent(
            new CustomEvent(ExclusionsLightGridEvents.REMOVE_EXCLUSION, {
              detail: {
                item: rowData.item,
              },
              bubbles: true,
              composed: true,
            })
          );
        }}"
        buttonColor="secondary"
        fontSize="14"
        buttonFill="quiet"
        icon="ds-icon-delete_large"
      ></gz-icon-button>
      <span></span
    ></span>`;
  }

  private removeExclusion(event: WebComponentEvent) {
    event.stopImmediatePropagation();

    const item: ExclusionGridItem = event.detail.item as ExclusionGridItem;

    this.exclusionsGridItems = this.exclusionsGridItems.filter(
      (exclusion: ExclusionGridItem): boolean => !isEqual(exclusion, item)
    );

    if (!this.exclusionsGridItems.length) {
      event.target.dispatchEvent(
        new CustomEvent(NetworkPopUpEvents.SET_CONFIRM_BUTTON, {
          bubbles: true,
          composed: true,
          detail: { value: true },
        })
      );
    }
  }

  private handleExclusionsSelectionChange(event: WebComponentEvent) {
    const selectedExclusionType = event.detail.selectedValue;
    // handle exclusion input validation
    switch (selectedExclusionType) {
      default:
        this.exclusionsFormInput.Validators = this.exclusionsGridItems.length
          ? []
          : [RequiredValidator(translateFn(`This field is required`))];
        break;
      case ExclusionsTypes.DATABASE:
        this.exclusionsFormInput.Validators = [
          RequiredValidator(translateFn(`This field is required`)),
          NotInValuesValidator(
            this.exclusionsGridItems
              .filter((item: ExclusionGridItem) => item.type === ExclusionsTypes.DATABASE)
              .map((item: ExclusionGridItem) => item.scope),
            translateFn('Database already exists')
          ),
        ];
        break;
      case ExclusionsTypes.MAILBOX:
        this.exclusionsFormInput.Validators = [
          RequiredValidator(translateFn(`Email is required`)),
          EmailValidator(translateFn(`Invalid email address`)),
          NotInValuesValidator(
            this.exclusionsGridItems
              .filter((item: ExclusionGridItem) => item.type === ExclusionsTypes.MAILBOX)
              .map((item: ExclusionGridItem) => item.scope),
            translateFn('Email already exists')
          ),
        ];
        break;
      case ExclusionsTypes.PUBLIC_FOLDER:
        this.exclusionsFormInput.Validators = [
          RequiredValidator(translateFn(`Folder path is required`)),
          CustomizableValidator((value: string): ValidationResult => this.validateFolderPath(value)),
          NotInValuesValidator(
            this.exclusionsGridItems
              .filter((item: ExclusionGridItem) => item.type === ExclusionsTypes.PUBLIC_FOLDER)
              .map((item: ExclusionGridItem) => item.scope),
            translateFn('Folder already exists')
          ),
        ];
        break;
    }
  }

  private validateFolderPath(path: string): ValidationResult {
    if (!path.startsWith('\\')) {
      return {
        valid: false,
        error: translateFn(`Paths must start with \\ character`),
      };
    }

    const windowsPathRegex =
      /^(\\|\\\\)(?:[a-zA-Z]:\\(?:[^<>:"\/\\|?*\n]+\\)*[^<>:"\/\\|?*\n]*|(?:[a-zA-Z0-9_.$-]+\\)+[^<>:"\/\\|?*\n]*|[?\.](?:\\Volume\{[a-zA-Z0-9-]+\}|\\[a-zA-Z]:)\\(?:[^<>:"\/\\|?*\n]+\\)*[^<>:"\/\\|?*\n]*)$/;

    const isPath = windowsPathRegex.test(path);
    return isPath
      ? { valid: true }
      : {
          valid: false,
          error: translateFn('Invalid folder path'),
        };
  }

  private setGridRowBorders() {
    const grid = this.exclusionsLightGrid.shadowRoot?.querySelector('#lightGrid');
    if (grid) {
      grid.setAttribute('theme', 'compact no-border');
    }
  }

  private addExclusion(event: WebComponentEvent) {
    const formGroupValue: FormGroupValue = this.exclusionsForm.value;

    if (!formGroupValue.exclusionsSingleSelect || !formGroupValue.exclusionsFormInput) {
      return;
    }

    this.exclusionsGridItems = [
      ...this.exclusionsGridItems,
      {
        type: formGroupValue.exclusionsSingleSelect as ExclusionsTypes,
        scope: formGroupValue.exclusionsFormInput as string,
      },
    ];

    this.exclusionsSingleSelect.Validators = [];
    this.exclusionsFormInput.Validators = [];

    this.exclusionsForm.value = {
      exclusionsSingleSelect: null,
      exclusionsFormInput: null,
    };

    event.target.dispatchEvent(
      new CustomEvent(NetworkPopUpEvents.SET_CONFIRM_BUTTON, {
        bubbles: true,
        composed: true,
        detail: { value: !this.formGroup.valid },
      })
    );
  }

  private getExchangeScanTaskDefaultName(): string {
    return translateFn('Exchange scan') + ' ' + getFormattedDateForTask();
  }

  private handleScanTargetsDropdownChange(event: WebComponentEvent) {
    const selectedScanTarget = event.detail.newValue as SelectOption;
    switch (selectedScanTarget.value) {
      case ExchangeTaskTargets.MAILBOXES_AND_PUBLIC_FOLDERS:
        this.objectsEmailCheckbox.Input.checked = true;
        this.objectsEmailCheckbox.Input.disabled = false;
        this.objectsContactsCheckbox.Input.checked = true;
        this.objectsContactsCheckbox.Input.disabled = false;
        this.objectsTasksCheckbox.Input.checked = true;
        this.objectsTasksCheckbox.Input.disabled = false;
        this.objectsAppointmentsCheckbox.Input.checked = true;
        this.objectsAppointmentsCheckbox.Input.disabled = false;
        this.objectsPostItemsCheckbox.Input.checked = true;
        this.objectsPostItemsCheckbox.Input.disabled = false;
        break;
      case ExchangeTaskTargets.MAILBOXES:
        this.objectsEmailCheckbox.Input.checked = true;
        this.objectsEmailCheckbox.Input.disabled = false;
        this.objectsContactsCheckbox.Input.checked = true;
        this.objectsContactsCheckbox.Input.disabled = false;
        this.objectsTasksCheckbox.Input.checked = true;
        this.objectsTasksCheckbox.Input.disabled = false;
        this.objectsAppointmentsCheckbox.Input.checked = true;
        this.objectsAppointmentsCheckbox.Input.disabled = false;
        this.objectsPostItemsCheckbox.Input.checked = false;
        this.objectsPostItemsCheckbox.Input.disabled = true;
        break;
      case ExchangeTaskTargets.PUBLIC_FOLDERS:
        this.objectsEmailCheckbox.Input.checked = true;
        this.objectsEmailCheckbox.Input.disabled = false;
        this.objectsContactsCheckbox.Input.checked = false;
        this.objectsContactsCheckbox.Input.disabled = true;
        this.objectsTasksCheckbox.Input.checked = false;
        this.objectsTasksCheckbox.Input.disabled = true;
        this.objectsAppointmentsCheckbox.Input.checked = false;
        this.objectsAppointmentsCheckbox.Input.disabled = true;
        this.objectsPostItemsCheckbox.Input.checked = true;
        this.objectsPostItemsCheckbox.Input.disabled = false;
        break;
    }

    this.handleObjectsCheckboxChange(event);
  }

  private handleFileTypesToScanOptionChanged(event: WebComponentEvent): void {
    const selectedFileType = event.detail.newValue as SelectOption;

    switch (selectedFileType.value) {
      case ExchangeTaskScanFileTypes.USER_DEFINED_EXTENSIONS:
      case ExchangeTaskScanFileTypes.ALL_FILES_EXCEPT_SPECIFIC_EXTENSIONS:
        this.extensionsForm.setDisabled(false);
        this.extensionsInput.Input.disabled = false;
        this.extensionsInput.Input.validationRegex = '^.?[^\\/:*?"<>| ]+$';
        elementClassListRemove(this.extensionsGroup, ElementClass.HIDDEN);
        break;
      case ExchangeTaskScanFileTypes.APPLICATIONS_ONLY:
      case ExchangeTaskScanFileTypes.ALL_FILES:
      default:
        this.extensionsForm.setDisabled(true);
        this.extensionsInput.Input.disabled = true;
        this.extensionsInput.value = [];
        elementClassListAdd(this.extensionsGroup, ElementClass.HIDDEN);
        break;
    }
  }

  private handleChipsChange(event: WebComponentEvent) {
    const extensions = event.detail.value as Array<string>;

    if (!extensions.length) {
      event.target.dispatchEvent(
        new CustomEvent('form-value-invalid', {
          bubbles: true,
          composed: true,
        })
      );

      event.target.dispatchEvent(
        new CustomEvent(NetworkPopUpEvents.SET_CONFIRM_BUTTON, {
          bubbles: true,
          composed: true,
          detail: { value: true },
        })
      );
    }
  }

  private handleExclusionsFormInputChange(event: WebComponentEvent) {
    if (!this.exclusionsSingleSelect.Validators.length && this.exclusionsFormInput.Input.value) {
      this.exclusionsSingleSelect.Validators = [RequiredValidator(translateFn(`Select exclusion type`))];
    }

    if (event.detail.sourceEvent && this.exclusionsGridItems.length && this.exclusionsSingleSelect.Validators.length) {
      this.exclusionsSingleSelect.Validators = [];
    }

    this.exclusionsSingleSelect.validate(true);
  }

  private scrollElementIntoView(element: HTMLElement) {
    setTimeout(() => {
      // Get the bounding rectangle of the element and its container
      const elementRect = element.getBoundingClientRect();
      const containerRect = this.container.getBoundingClientRect();

      // Calculate the top position of the element relative to the container's scroll position
      const offsetTop = elementRect.top - containerRect.top + this.container.scrollTop;

      this.parentElement?.scrollTo({
        top: offsetTop,
        behavior: 'smooth',
      });
    }, 10);
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'gz-exchange-scan-task': GzExchangeScanTaskWebComponent;
  }
}
